enum QuestionEventTriggerType {
  EMAIL
}

enum QuestionType {
  FIVE_SCALE
  TEN_SCALE
  FREETEXT
  BOOL
  SINGLE
  MULTIPLE
  ZERO_TEN_SCALE
}

enum OrderDirection {
  ASC
  DESC
}

type QuestionEventTrigger {
  type: QuestionEventTriggerType # Reference to the QuestionEventTriggerType enum
  gt: String # Represents a value that must be greater than this
  lt: String # Represents a value that must be lesser than this
  choice_trigger: [String] # List of choices that the trigger should react to
}

input SurveyFiltersInput {
  advisor_ids: [ID!]
  bookmark: Int
  case_completion_end_date: String
  case_completion_start_date: String
  case_submission_end_date: String
  case_submission_start_date: String
  case_types: [String!]
  network_id: ID
  order: OrderDirection
  order_by: String
  order_by_question_id: ID
  organisation_id: ID
  org_ids: [ID!]
  page_size: Int
}

type SurveyFilters {
  advisor_ids: [ID!]
  bookmark: Int
  case_completion_end_date: String
  case_completion_start_date: String
  case_submission_end_date: String
  case_submission_start_date: String
  case_types: [String!]
  network_id: ID
  order: OrderDirection
  order_by: String
  order_by_question_id: ID
  organisation_id: ID
  org_ids: [ID!]
  page_size: Int
}

type DependencyConditionArray {
  dependent_question_id: String
  equals_bool: Boolean
  equals_choice: [String]
  greater_than: Int
  less_than: Int
}

type DependencyCondition {
  conditions: [DependencyConditionArray]
}

interface QuestionI {
  id: ID! # id holds the uuidv4 of the question entity.
  question: String # question is the question content
  version: Int # version is the version number of the entity in the blockchain
  type: QuestionType # type holds the type of the question
  event_trigger: QuestionEventTrigger # event_trigger is holding the event information
  choices: [String] # choices contains a list of choices of the question
  order: Int # order holds a random integer value used for ordering the questions
  required: Boolean # required represents if the user is required or not to answer the question
  topic: String # topic is the topic of the question
  answer: Answers # answer holds the answer of the question
  answered: Boolean # answered represents if the user has answered the question
  show_if: [DependencyCondition]
}

type SurveyQuestion implements QuestionI {
  id: ID! # id holds the uuidv4 of the question entity.
  question: String # question is the question content
  version: Int # version is the version number of the entity in the blockchain
  type: QuestionType # type holds the type of the question
  event_trigger: QuestionEventTrigger # event_trigger is holding the event information
  choices: [String] # choices contains a list of choices of the question
  order: Int # order holds a random integer value used for ordering the questions
  required: Boolean # required represents if the user is required or not to answer the question
  topic: String # topic is the topic of the question
  answer: Answers # answer holds the answer of the question
  answered: Boolean # answered represents if the user has answered the question
  show_if: [DependencyCondition]
}

input SurveyQuestionInput {
  id: ID! # id holds the uuidv4 of the question entity.
  question: String # question is the question content
  version: Int # version is the version number of the entity in the blockchain
  type: QuestionType # type holds the type of the question
  event_trigger: QuestionEventTriggerInput # event_trigger is holding the event information
  choices: [String] # choices contains a list of choices of the question
  order: Int # order holds a random integer value used for ordering the questions
  required: Boolean # required represents if the user is required or not to answer the question
  topic: String # topic is the topic of the question
  answered: Boolean # answered represents if the user has answered the question
  show_if: [DependencyConditionInput]
}

input QuestionEventTriggerInput {
  type: QuestionEventTriggerType # Reference to the QuestionEventTriggerType enum
  gt: String # Represents a value that must be greater than this
  lt: String # Represents a value that must be lesser than this
  choice_trigger: [String] # List of choices that the trigger should react to
}

input DependencyConditionInput {
  conditions: [DependencyConditionArrayInput!]
}

input DependencyConditionArrayInput {
  dependent_question_id: String
  equals_bool: Boolean
  equals_choice: [String]
  greater_than: Int
  less_than: Int
}

type Survey {
  questions: [SurveyQuestion] # questions is a list of questions in the survey
}

type SurveyAnswer {
  answers: [Answers] # answers is a list of answers to the questions in the survey
}

type Answers {
  question_id: ID! # question_id holds the uuidv4 of the question entity.
  question_version: Int # question_version is the version number of the entity in the blockchain
  five_scale_answer: Int # five_scale_answer holds the answer of the question
  ten_scale_answer: Int # ten_scale_answer holds the answer of the question
  zero_ten_scale_answer: Int # ten_scale_answer holds the answer of the question
  free_text_answer: String # free_text_answer holds the answer of the question
  bool_answer: Boolean # bool_answer holds the answer of the question
  single_choice_answer: String # single_choice_answer holds the answer of the question
  multiple_choice_answer: [String] # multiple_choice_answer holds the answer of the question
}

type PageInfo {
  bookmark: Int
}

type Question {
  id: ID
  question: String
  type: QuestionType
  topic: String
  order: Int
}

type QuestionAnswer {
  answer: Answer
  question: Question
}

type SurveyResult {
  advisor_id: ID!
  advisor_name: String!
  case_completion_date: String
  case_id: ID!
  case_submission_date: String
  case_type: String!
  client_id: ID!
  client_name: String!
  network_id: ID!
  org_id: ID!
  question_answers: [QuestionAnswer]
  completed_at: String
  modified_at: String
}

type SurveyResultsResponse {
  filters: SurveyFilters
  next_page: PageInfo
  survey_results: [SurveyResult]
}

input SurveyResultsInput {
  advisor_ids: [ID!]
  bookmark: Int
  case_completion_end_date: String
  case_completion_start_date: String
  case_submission_end_date: String
  case_submission_start_date: String
  case_types: [String!]
  network_id: ID
  order: OrderDirection
  order_by: String
  order_by_question_id: ID
  organisation_id: ID
  org_ids: [ID!]
  page_size: Int
}
